// @flow

import { useContext } from 'react';

import { useFormContext } from 'react-hook-form';

import { View } from 'reshaped';

import { TextField } from 'src/Reshaped/Form/TextField';
import { CountrySelect } from 'src/components/Form/CountrySelect';

import { Email } from 'src/components/Form/Email';

import ContactFormContext from './ContactFormContext';
import { CurrencySelect } from './components/CurrencySelect';
import { OrgaTypePills } from './components/OrgaTypePills';
import { Phone } from './components/Phone';
import { StateSelect } from './components/StateSelect';
import { Zip } from './components/Zip';
import { useRequiredRule } from './hooks/useRequiredRule';
import type { FormField } from './types';

type Props = { field: FormField };

export function Field({
  field: { help, label, name, required = true, placeholder, disabled = false },
}: Props): React.MixedElement {
  const { role } = useContext(ContactFormContext);
  const { control, formState } = useFormContext();
  const requiredRule = useRequiredRule(required);

  const fieldProps = {
    control,
    name,
    label,
    placeholder,
    required,
    disabled,
    help,
    rules: { ...requiredRule },
  };

  let containerProps;
  let input;
  switch (name) {
    case 'country':
      input = <CountrySelect {...fieldProps} />;
      break;
    case 'state':
      input = <StateSelect {...fieldProps} />;
      break;
    case 'currency':
      input = <CurrencySelect {...fieldProps} />;
      break;
    case 'email':
      input = <Email {...fieldProps} />;
      break;
    case 'zip':
      input = <Zip {...fieldProps} />;
      containerProps = {
        attributes: {
          style: {
            flexGrow: 0,
            minWidth: '15ch',
          },
        },
      };
      break;
    case 'phone':
    case 'fax':
      input = <Phone {...fieldProps} />;
      break;
    case 'type':
      input = (
        <OrgaTypePills
          showIndividualType={
            formState.defaultValues?.type === 'individual' ||
            role === 'CUSTOMER'
          }
        />
      );
      break;
    default:
      input = (
        <TextField
          name={name}
          label={label}
          control={control}
          disabled={disabled}
          rules={{ ...requiredRule }}
          inputProps={{ placeholder }}
          help={help}
        />
      );
  }

  return (
    <View.Item grow {...containerProps}>
      {input}
    </View.Item>
  );
}
